// admin/src/pages/ScimIntegrations/OutboundTargetDialog.js

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  Box,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import {
  createOutboundTarget,
  updateOutboundTarget,
  fetchGroupsList,
  fetchUsersList,
  extractOutboundErrorMessage,
} from './scimOutboundApi';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function OutboundTargetDialog({
  open,
  onClose,
  onSuccess,
  target = null, // null for create, object for edit
}) {
  const isEdit = Boolean(target && target._id);

  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [token, setToken] = useState('');
  const [compatMode, setCompatMode] = useState('default');
  const [scopeType, setScopeType] = useState('group'); // 'group', 'users', or 'all'
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [confirmAllText, setConfirmAllText] = useState('');
  const [excludeInboundProvisioned, setExcludeInboundProvisioned] = useState(false);
  const [onRemoval, setOnRemoval] = useState('deactivate');
  const [dryRun, setDryRun] = useState(true); // default true on create
  const [rateLimitPerMin, setRateLimitPerMin] = useState(120);

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Load available groups and users when dialog opens
  useEffect(() => {
    if (open) {
      let isMounted = true;
      setLoadingGroups(true);
      fetchGroupsList()
        .then((res) => {
          if (!isMounted) return;
          const list = Array.isArray(res) ? res : (res?.data || []);
          setGroups(list);
        })
        .catch(() => {
          if (isMounted) setGroups([]);
        })
        .finally(() => {
          if (isMounted) setLoadingGroups(false);
        });

      return () => {
        isMounted = false;
      };
    }
  }, [open]);

  // Load available users when scope is 'users'
  useEffect(() => {
    if (open && scopeType === 'users' && users.length === 0) {
      let isMounted = true;
      setLoadingUsers(true);
      fetchUsersList()
        .then((res) => {
          if (!isMounted) return;
          const list = Array.isArray(res) ? res : (res?.data || []);
          setUsers(list);
        })
        .catch(() => {
          if (isMounted) setUsers([]);
        })
        .finally(() => {
          if (isMounted) setLoadingUsers(false);
        });

      return () => {
        isMounted = false;
      };
    }
  }, [open, scopeType, users.length]);

  // Reset or initialize form data
  useEffect(() => {
    if (!open) {
      setToken('');
      setConfirmAllText('');
      setError(null);
      return;
    }

    if (isEdit && target) {
      setName(target.name || '');
      setBaseUrl(target.baseUrl || '');
      setToken('');
      setCompatMode(target.compatMode || 'default');
      const st = target.scope?.type === 'all'
        ? 'all'
        : target.scope?.type === 'users'
          ? 'users'
          : 'group';
      setScopeType(st);
      setSelectedGroupIds(Array.isArray(target.scope?.groupIds) ? target.scope.groupIds : []);
      setSelectedUserIds(Array.isArray(target.scope?.userIds) ? target.scope.userIds.map(String) : []);
      setConfirmAllText('');
      setExcludeInboundProvisioned(Boolean(target.excludeInboundProvisioned));
      setOnRemoval(target.onRemoval || 'deactivate');
      setDryRun(target.dryRun !== undefined ? target.dryRun : true);
      setRateLimitPerMin(target.rateLimitPerMin || 120);
    } else {
      setName('');
      setBaseUrl('');
      setToken('');
      setCompatMode('default');
      setScopeType('group');
      setSelectedGroupIds([]);
      setSelectedUserIds([]);
      setConfirmAllText('');
      setExcludeInboundProvisioned(false);
      setOnRemoval('deactivate');
      setDryRun(true); // default true on create
      setRateLimitPerMin(120);
    }
    setError(null);
  }, [open, isEdit, target]);

  const isDryRunForced = !isEdit && scopeType === 'all';

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Validation
    if (!name.trim()) {
      setError('Target name is required.');
      return;
    }
    if (!baseUrl.trim()) {
      setError('Base URL is required.');
      return;
    }
    if (!baseUrl.trim().startsWith('https://')) {
      setError('Base URL must begin with https://');
      return;
    }
    if (!isEdit && !token.trim()) {
      setError('Bearer token is required.');
      return;
    }

    if (scopeType === 'group' && selectedGroupIds.length === 0) {
      setError('Please select at least one group for scope.');
      return;
    }
    if (scopeType === 'users' && selectedUserIds.length === 0) {
      setError('Please select at least one user for scope.');
      return;
    }
    if (scopeType === 'all') {
      const trimmedConfirm = confirmAllText.trim();
      const targetNameTrimmed = name.trim();
      const isConfirmed = isEdit
        ? (targetNameTrimmed && trimmedConfirm === targetNameTrimmed)
        : (trimmedConfirm === 'ALL USERS' || (targetNameTrimmed && trimmedConfirm === targetNameTrimmed));

      if (!isConfirmed) {
        setError(
          isEdit
            ? `Please type "${targetNameTrimmed}" to confirm provisioning all users.`
            : 'Please type the target name or "ALL USERS" to confirm provisioning all users.'
        );
        return;
      }
    }

    setBusy(true);
    setError(null);

    try {
      const payload = {
        name: name.trim(),
        baseUrl: baseUrl.trim(),
        compatMode,
        scope: {
          type: scopeType,
          groupIds: scopeType === 'group' ? selectedGroupIds : [],
          userIds: scopeType === 'users' ? selectedUserIds : [],
        },
        excludeInboundProvisioned,
        onRemoval,
        dryRun: isDryRunForced ? true : dryRun,
        rateLimitPerMin: Number(rateLimitPerMin) || 120,
      };

      if (scopeType === 'all') {
        payload.confirmAll = true;
      }

      if (!isEdit) {
        payload.token = token;
      }

      if (isEdit) {
        await updateOutboundTarget(target._id, payload);
      } else {
        await createOutboundTarget(payload);
      }

      // Token fields: autoComplete="off", cleared after submit, never logged.
      setToken('');
      setConfirmAllText('');
      onSuccess();
      onClose();
    } catch (err) {
      setError(extractOutboundErrorMessage(err));
    } finally {
      setBusy(false);
      // Ensure token is wiped if error occurred during creation
      setToken('');
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle id="outbound-target-dialog-title">
        {isEdit ? 'Edit Outbound Target' : 'Add Outbound Target'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Narrowing warning when editing an all-scope target to a narrower scope */}
          {isEdit && target?.scope?.type === 'all' && scopeType !== 'all' && (
            <Alert severity="warning" id="outbound-scope-narrowing-warning">
              Users leaving scope are deactivated or deleted in the external app according to On removal.
            </Alert>
          )}

          <TextField
            label="Target Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            fullWidth
            required
            id="outbound-target-name-input"
            helperText="Human-friendly label for this destination (e.g. Okta Production, Zendesk)"
          />

          <TextField
            label="Base URL"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            disabled={busy}
            fullWidth
            required
            id="outbound-target-baseurl-input"
            helperText="SCIM 2.0 endpoint base URL. Must start with https:// (e.g. https://idp.example.com/scim/v2)"
          />

          {!isEdit && (
            <TextField
              label="Bearer Token"
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              disabled={busy}
              fullWidth
              required
              id="outbound-target-token-input"
              helperText="Write-only token. Never logged; only last 4 characters are retained for verification."
            />
          )}

          <FormControl fullWidth>
            <InputLabel id="outbound-compat-mode-label">Compatibility Mode</InputLabel>
            <Select
              labelId="outbound-compat-mode-label"
              id="outbound-target-compat-mode-select"
              value={compatMode}
              label="Compatibility Mode"
              onChange={(e) => setCompatMode(e.target.value)}
              disabled={busy}
            >
              <MenuItem value="default">Default (Standard SCIM 2.0 PATCH & PUT)</MenuItem>
              <MenuItem value="patch-only">PATCH Only (Use SCIM PATCH for user updates)</MenuItem>
              <MenuItem value="put-only">PUT Only (Always send full User resource)</MenuItem>
            </Select>
          </FormControl>

          {/* Scope selection: 'group', 'users', or 'all' */}
          <FormControl fullWidth>
            <InputLabel id="outbound-scope-type-label">Provisioning Scope</InputLabel>
            <Select
              labelId="outbound-scope-type-label"
              id="outbound-target-scope-type-select"
              inputProps={{ id: 'outbound-target-scope-type-select-input' }}
              value={scopeType}
              label="Provisioning Scope"
              onChange={(e) => {
                const newType = e.target.value;
                setScopeType(newType);
                if (newType === 'all' && !isEdit) {
                  setDryRun(true);
                }
              }}
              disabled={busy}
            >
              <MenuItem value="group">Group Based (Users belonging to selected groups)</MenuItem>
              <MenuItem value="users">Specific users</MenuItem>
              <MenuItem value="all">All users</MenuItem>
            </Select>
          </FormControl>

          {scopeType === 'all' && (
            <Stack spacing={2}>
              <Alert severity="warning" id="outbound-scope-all-warning">
                This will send EVERY active IAM user to the external app. New and changed targets start in dry run. Review the planned operations before going live.
              </Alert>

              <TextField
                label={isEdit ? `Type "${name.trim()}" to confirm` : 'Type target name or "ALL USERS" to confirm'}
                value={confirmAllText}
                onChange={(e) => setConfirmAllText(e.target.value)}
                disabled={busy}
                fullWidth
                required
                id="outbound-target-confirm-all-input"
                helperText={
                  isEdit
                    ? `To confirm provisioning all users, type "${name.trim()}" exactly.`
                    : 'To confirm provisioning all users, type the target name or "ALL USERS".'
                }
              />
            </Stack>
          )}

          {scopeType === 'group' && (
            <FormControl fullWidth>
              <InputLabel id="outbound-groups-select-label">Select Groups</InputLabel>
              <Select
                labelId="outbound-groups-select-label"
                id="outbound-target-groups-select"
                multiple
                value={selectedGroupIds}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedGroupIds(typeof val === 'string' ? val.split(',') : val);
                }}
                input={<OutlinedInput label="Select Groups" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => {
                      const grp = groups.find((g) => g._id === val || g.id === val);
                      return <Chip key={val} size="small" label={grp?.name || grp?.groupName || val} />;
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
                disabled={busy || loadingGroups}
              >
                {loadingGroups ? (
                  <MenuItem disabled>Loading groups...</MenuItem>
                ) : groups.length === 0 ? (
                  <MenuItem disabled>No groups found</MenuItem>
                ) : (
                  groups.map((grp) => {
                    const id = grp._id || grp.id;
                    const grpName = grp.name || grp.groupName || id;
                    return (
                      <MenuItem key={id} value={id}>
                        <Checkbox checked={selectedGroupIds.indexOf(id) > -1} />
                        <ListItemText primary={grpName} />
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </FormControl>
          )}

          {scopeType === 'users' && (
            <Autocomplete
              multiple
              freeSolo
              id="outbound-target-users-select"
              options={users}
              getOptionLabel={(option) => {
                if (typeof option === 'string') {
                  const u = users.find((usr) => String(usr._id || usr.id) === option);
                  return u?.username || option;
                }
                return option.username || String(option._id || option.id || '');
              }}
              isOptionEqualToValue={(option, value) => {
                const optId = String(option._id || option.id || option);
                const valId = String(value._id || value.id || value);
                return optId === valId;
              }}
              value={selectedUserIds.map((id) => users.find((u) => String(u._id || u.id) === String(id)) || { _id: id, username: id })}
              onChange={(_, newValues) => {
                const ids = newValues.map((v) => (typeof v === 'string' ? v : String(v._id || v.id || ''))).filter(Boolean);
                setSelectedUserIds(ids);
              }}
              disabled={busy || loadingUsers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Users"
                  placeholder={loadingUsers ? 'Loading users...' : 'Search users by username...'}
                  inputProps={{
                    ...params.inputProps,
                    id: 'outbound-target-user-ids-input',
                    onChange: (e) => {
                      if (params.inputProps?.onChange) {
                        params.inputProps.onChange(e);
                      }
                      const raw = e.target.value;
                      if (raw && raw.includes(',')) {
                        const parsed = raw.split(',').map((s) => s.trim()).filter(Boolean);
                        if (parsed.length > 0) {
                          setSelectedUserIds(parsed);
                        }
                      }
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.username || option._id || option}
                    size="small"
                    {...getTagProps({ index })}
                    key={String(option._id || option.id || option)}
                  />
                ))
              }
            />
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="outbound-on-removal-label">On Scope Removal</InputLabel>
              <Select
                labelId="outbound-on-removal-label"
                id="outbound-target-on-removal-select"
                value={onRemoval}
                label="On Scope Removal"
                onChange={(e) => setOnRemoval(e.target.value)}
                disabled={busy}
              >
                <MenuItem value="deactivate">Deactivate (active: false)</MenuItem>
                <MenuItem value="delete">Hard Delete (DELETE /Users/:id)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Rate Limit (Req/Min)"
              type="number"
              value={rateLimitPerMin}
              onChange={(e) => setRateLimitPerMin(e.target.value)}
              disabled={busy}
              fullWidth
              id="outbound-target-rate-limit-input"
              inputProps={{ min: 1, max: 600 }}
              helperText="Maximum requests per minute to this target"
            />
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={excludeInboundProvisioned}
                onChange={(e) => setExcludeInboundProvisioned(e.target.checked)}
                disabled={busy}
                id="outbound-target-exclude-inbound-switch"
              />
            }
            label="Exclude Inbound-Provisioned Users (Prevent synchronization loops)"
          />

          {/* Dry Run Setting */}
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isDryRunForced ? true : dryRun}
                  onChange={(e) => {
                    if (!isDryRunForced) setDryRun(e.target.checked);
                  }}
                  disabled={busy || isDryRunForced}
                  color="warning"
                  id="outbound-target-dry-run-switch"
                  inputProps={{ id: 'outbound-target-dry-run-switch-input', 'aria-label': 'dry-run-switch' }}
                />
              }
              label={
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Dry Run Mode (Simulate operations without making changes to target)
                </Typography>
              }
            />
            {isDryRunForced && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} id="outbound-dry-run-forced-text">
                Dry run is forced on for scope "All users" when creating a target. You can review planned operations before going live.
              </Typography>
            )}
            {!isDryRunForced && !dryRun && (
              <Alert severity="warning" sx={{ mt: 1.5 }} id="outbound-dry-run-warning">
                <strong>LIVE PROVISIONING ACTIVE:</strong> Real changes will be sent to the remote SCIM provider on the next sync cycle.
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={busy}
          id="outbound-target-submit-btn"
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          {isEdit ? 'Save Changes' : 'Create Target'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
